import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'sonner';
import api from '../api';
import { UserContext } from '../context/UserContext';
import ReglasContables from './ReglasContables';

const MESES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
               'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const estadoConfig = {
  en_progreso: { label: 'En progreso', color: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500' },
  completado:  { label: 'Completado',  color: 'bg-green-100 text-green-800',   dot: 'bg-green-500'  },
  error:       { label: 'Error',       color: 'bg-red-100 text-red-800',       dot: 'bg-red-500'    },
  cancelado:   { label: 'Cancelado',   color: 'bg-gray-100 text-gray-700',     dot: 'bg-gray-400'   }
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
export default function PanelContabilidad() {
  const { currentUser } = useContext(UserContext);
  const isAdmin = currentUser?.role === 'ADMIN';

  // Período global (mes/año)
  const hoy = new Date();
  const mesDefault  = hoy.getMonth() === 0 ? 12 : hoy.getMonth();
  const anioDefault = hoy.getMonth() === 0 ? hoy.getFullYear() - 1 : hoy.getFullYear();
  const [mes,  setMes]  = useState(mesDefault);
  const [anio, setAnio] = useState(anioDefault);

  // Tab activa
  const [tab, setTab] = useState('listas');

  // Datos
  const [listas,         setListas]         = useState([]);
  const [rentaCompanies, setRentaCompanies] = useState([]);  // todos los clientes con renta=true
  const [configMap,      setConfigMap]      = useState({});  // { clienteId: ConfigEmpresaContable }
  const [lastLogMap,     setLastLogMap]     = useState({});  // { clienteId-tipo: log }
  const [logs,           setLogs]           = useState([]);
  const [usuarios,       setUsuarios]       = useState([]);
  const [loading,        setLoading]        = useState(true);

  // Editor de lista (null = no mostrando, objeto = creando/editando)
  const [editingLista, setEditingLista] = useState(null);

  // Lista activa (vista detalle)
  const [listaActiva, setListaActiva] = useState(null);

  // Config empresa expandida (tab "Config Empresas")
  const [expandedConfig, setExpandedConfig] = useState(null);
  const [configSearch,   setConfigSearch]   = useState('');

  // Credenciales globales Inputsoft
  const [configGlobal,     setConfigGlobal]     = useState({ inputsoftRutEmpresa: '', inputsoftUsuario: '', inputsoftPassword: '', inputsoftPasswordSet: false });
  const [guardandoGlobal,  setGuardandoGlobal]  = useState(false);

  useEffect(() => { cargarDatos(); }, []);
  useEffect(() => { if (tab === 'credenciales') cargarConfigGlobal(); }, [tab]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const requests = [
        api.get('/listados/all'),
        api.get('/contabilidad/empresas'),
        api.get('/contabilidad/listas'),
        api.get('/contabilidad/logs'),
      ];
      if (isAdmin) requests.push(api.get('/contabilidad/usuarios'));

      const results = await Promise.allSettled(requests);
      const get = (r) => (r?.status === 'fulfilled' ? r.value : null);

      const clientesRes = get(results[0]);
      const configsRes  = get(results[1]);
      const listasRes   = get(results[2]);
      const logsRes     = get(results[3]);

      if (isAdmin && get(results[4])) setUsuarios(get(results[4]).data || []);

      // Mapa config por clienteId (se construye primero para filtrar empresas visibles)
      const cMap = {};
      if (configsRes) {
        for (const cfg of (configsRes.data || [])) {
          if (cfg.clienteId?._id) cMap[cfg.clienteId._id] = cfg;
        }
        setConfigMap(cMap);
      }

      // Empresas renta: ADMIN ve todas, FACTURACION solo las asignadas (las que tienen config)
      if (clientesRes) {
        const todasRenta = (clientesRes.data || [])
          .filter(c => c.renta)
          .sort((a, b) => a.nombre.localeCompare(b.nombre));
        const visibles = isAdmin
          ? todasRenta
          : todasRenta.filter(c => cMap[c._id]);
        setRentaCompanies(visibles);
      }

      // Mapa último log por clienteId-tipo
      if (logsRes) {
        const logsData = logsRes.data || [];
        const lMap = {};
        for (const log of logsData) {
          const cid = log.clienteId?._id;
          if (!cid) continue;
          const key = `${cid}-${log.tipoDocumento}`;
          if (!lMap[key]) lMap[key] = log;
        }
        setLastLogMap(lMap);
        setLogs(logsData);
      }

      if (listasRes) {
        setListas(listasRes.data || []);
      }

      // Log de errores parciales en consola
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          const names = ['/listados/all', '/contabilidad/empresas', '/contabilidad/listas', '/contabilidad/logs', '/contabilidad/usuarios'];
          console.warn(`[cargarDatos] Falló ${names[i] || i}:`, r.reason?.message || r.reason);
        }
      });
    } catch (err) {
      console.error('Error cargando datos contabilidad:', err);
    } finally {
      setLoading(false);
    }
  };

  const cargarConfigGlobal = async () => {
    try {
      const { data } = await api.get('/contabilidad/config-global');
      setConfigGlobal({ ...data, inputsoftPassword: '' });
    } catch (_) {}
  };

  const eliminarLista = async (listaId) => {
    if (!window.confirm('¿Eliminar esta lista? Esta acción no se puede deshacer.')) return;
    try {
      await api.delete(`/contabilidad/listas/${listaId}`);
      toast.success('Lista eliminada');
      cargarDatos();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error eliminando lista');
    }
  };

  const ejecutarLista = async (lista, tipo) => {
    // Filtrar empresas con ID Inputsoft configurado
    const empresasConConfig = (lista.empresas || []).filter(e => {
      const id = e._id || e;
      return configMap[id]?.inputsoftEmpresaId;
    });

    if (empresasConConfig.length === 0) {
      toast.error('Ninguna empresa de la lista tiene ID Inputsoft configurado.\nConfigúralas primero en la pestaña "Config. Empresas".');
      return;
    }

    // Para 'ambos', ejecutar compras y ventas de cada empresa
    const tipos = tipo === 'ambos' ? ['compras', 'ventas'] : [tipo];
    let ok = 0, fail = 0;

    toast.info(`Iniciando ${empresasConConfig.length * tipos.length} proceso(s)...`);

    for (const empresa of empresasConConfig) {
      for (const t of tipos) {
        try {
          await api.post('/contabilidad/ejecutar', {
            clienteId: empresa._id || empresa,
            mes:  parseInt(mes),
            anio: parseInt(anio),
            tipoDocumento: t
          });
          ok++;
        } catch (err) {
          fail++;
          console.error(`Error ${empresa.nombre} ${t}:`, err.response?.data?.error);
        }
      }
    }

    toast.success(
      `${ok} proceso(s) iniciados${fail > 0 ? `, ${fail} ya en curso o error` : ''} — ${MESES[mes]} ${anio}`
    );
    setTimeout(cargarDatos, 2500);
  };

  const guardarConfigGlobal = async () => {
    setGuardandoGlobal(true);
    try {
      await api.put('/contabilidad/config-global', configGlobal);
      toast.success('Credenciales guardadas');
      cargarConfigGlobal();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error guardando');
    } finally {
      setGuardandoGlobal(false);
    }
  };

  const anioActual = new Date().getFullYear();
  const años = Array.from({ length: 5 }, (_, i) => anioActual - i);

  const filteredConfigCompanies = rentaCompanies.filter(c =>
    !configSearch ||
    c.nombre.toLowerCase().includes(configSearch.toLowerCase()) ||
    c.rut.includes(configSearch)
  );

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto">

      {/* Cabecera con período */}
      <div className="flex flex-wrap items-start sm:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Contabilidad</h1>
          <p className="text-xs sm:text-sm text-gray-400">Automatización Libro Compras/Ventas SII → Inputsoft</p>
        </div>
        <div className="flex items-center gap-2 bg-white border rounded-xl px-3 py-2 shadow-sm">
          <span className="text-xs text-gray-400 font-medium uppercase tracking-wide hidden sm:inline">Período</span>
          <select value={mes} onChange={e => setMes(Number(e.target.value))}
            className="border-0 text-sm font-semibold text-gray-700 focus:ring-0 bg-transparent">
            {MESES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
          <select value={anio} onChange={e => setAnio(Number(e.target.value))}
            className="border-0 text-sm font-semibold text-gray-700 focus:ring-0 bg-transparent">
            {años.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0.5 border-b mb-5 overflow-x-auto">
        {[
          { id: 'listas',       label: '📂 Mis Listas'      },
          { id: 'empresas',     label: '⚙ Config. Empresas' },
          { id: 'historial',    label: '📋 Historial'        },
          { id: 'credenciales', label: '🔑 Credenciales'     },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === t.id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── TAB: MIS LISTAS ────────────────────────────────────────────────── */}
      {tab === 'listas' && (
        <div>
          {editingLista ? (
            <ListaEditor
              lista={editingLista}
              companies={rentaCompanies}
              configMap={configMap}
              onSave={async (data) => {
                try {
                  if (data._id) {
                    await api.put(`/contabilidad/listas/${data._id}`, data);
                    toast.success('Lista actualizada');
                  } else {
                    await api.post('/contabilidad/listas', data);
                    toast.success('Lista creada');
                  }
                  setEditingLista(null);
                  cargarDatos();
                } catch (err) {
                  toast.error(err.response?.data?.error || 'Error guardando lista');
                }
              }}
              onCancel={() => setEditingLista(null)}
            />
          ) : listaActiva ? (
            <ListaDetalle
              lista={listaActiva}
              mes={mes}
              anio={anio}
              rentaCompanies={rentaCompanies}
              configMap={configMap}
              logs={logs}
              usuarios={usuarios}
              isAdmin={isAdmin}
              onBack={() => setListaActiva(null)}
              onEdit={() => {
                setEditingLista({
                  _id:         listaActiva._id,
                  nombre:      listaActiva.nombre,
                  descripcion: listaActiva.descripcion,
                  empresas:    (listaActiva.empresas || []).map(e => (e._id || e).toString())
                });
                setListaActiva(null);
              }}
              onDelete={() => { eliminarLista(listaActiva._id); setListaActiva(null); }}
              onEjecutar={(tipo) => ejecutarLista(listaActiva, tipo)}
              onGuardado={cargarDatos}
            />
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500">
                  {loading ? 'Cargando...' : listas.length === 0 ? 'Sin listas aún.' : `${listas.length} lista(s)`}
                </p>
                <button
                  onClick={() => setEditingLista({ nombre: '', descripcion: '', empresas: [] })}
                  className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  + Nueva Lista
                </button>
              </div>

              {listas.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border">
                  <div className="text-5xl mb-3">📂</div>
                  <p className="text-gray-600 font-medium mb-1">Crea tu primera lista</p>
                  <p className="text-sm text-gray-400 mb-5">
                    Agrupa empresas por tipo de proceso: IVA, RCV, F29, etc.<br />
                    Luego ejecútalas juntas con un solo clic.
                  </p>
                  <button
                    onClick={() => setEditingLista({ nombre: '', descripcion: '', empresas: [] })}
                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
                    + Nueva Lista
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {listas.map(lista => (
                    <ListaCard
                      key={lista._id}
                      lista={lista}
                      mes={mes}
                      anio={anio}
                      configMap={configMap}
                      onAbrir={() => setListaActiva(lista)}
                      onEdit={() => setEditingLista({
                        _id:         lista._id,
                        nombre:      lista.nombre,
                        descripcion: lista.descripcion,
                        empresas:    (lista.empresas || []).map(e => (e._id || e).toString())
                      })}
                      onDelete={() => eliminarLista(lista._id)}
                      onEjecutar={(tipo) => ejecutarLista(lista, tipo)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── TAB: CONFIG EMPRESAS ──────────────────────────────────────────── */}
      {tab === 'empresas' && (
        <div>
          <div className="flex flex-wrap gap-3 items-center mb-4">
            <input
              type="text"
              value={configSearch}
              onChange={e => setConfigSearch(e.target.value)}
              placeholder="Buscar empresa o RUT..."
              className="flex-1 min-w-0 border rounded-lg px-3 py-2 text-sm" />
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {Object.keys(configMap).length} configuradas / {rentaCompanies.length} total
            </span>
          </div>

          {loading ? (
            <p className="text-sm text-gray-400 text-center py-10">Cargando...</p>
          ) : filteredConfigCompanies.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">Sin resultados.</p>
          ) : (
            <div className="space-y-2">
              {filteredConfigCompanies.map(c => {
                const cfg       = configMap[c._id];
                const isExpanded = expandedConfig === c._id;
                const tieneId   = !!cfg?.inputsoftEmpresaId;
                return (
                  <div key={c._id} className="bg-white border rounded-xl overflow-hidden shadow-sm">
                    <button
                      onClick={() => setExpandedConfig(isExpanded ? null : c._id)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${tieneId ? 'bg-green-500' : 'bg-orange-400'}`} />
                        <div className="min-w-0">
                          <div className="font-medium text-gray-800 text-sm truncate">{c.nombre}</div>
                          <div className="text-xs text-gray-400 font-mono">{c.rut}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                        {tieneId ? (
                          <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full font-medium hidden sm:inline">
                            #{cfg.inputsoftEmpresaId}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 bg-orange-50 text-orange-600 border border-orange-200 rounded-full hidden sm:inline">
                            Sin configurar
                          </span>
                        )}
                        <span className="text-gray-400 text-xs">{isExpanded ? '▲' : '▼'}</span>
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t bg-gray-50/50">
                        <div className="pt-4">
                          <ConfigEmpresaInline
                            clienteId={c._id}
                            initialConfig={cfg}
                            usuarios={usuarios}
                            isAdmin={isAdmin}
                            onGuardado={cargarDatos}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: HISTORIAL ───────────────────────────────────────────────── */}
      {tab === 'historial' && <HistorialLogs logs={logs} onRefresh={cargarDatos} />}

      {/* ── TAB: CREDENCIALES ────────────────────────────────────────────── */}
      {tab === 'credenciales' && (
        <div className="bg-white border rounded-xl p-4 sm:p-6 max-w-2xl shadow-sm">
          <h3 className="font-semibold text-gray-700 mb-1">Credenciales Inputsoft</h3>
          <p className="text-xs text-gray-400 mb-4">Usadas para todas las empresas. La contraseña se guarda cifrada.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">RUT empresa operador</label>
              <input type="text" value={configGlobal.inputsoftRutEmpresa}
                onChange={e => setConfigGlobal(p => ({ ...p, inputsoftRutEmpresa: e.target.value }))}
                placeholder="77.596.210-0" className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Usuario</label>
              <input type="text" value={configGlobal.inputsoftUsuario}
                onChange={e => setConfigGlobal(p => ({ ...p, inputsoftUsuario: e.target.value }))}
                placeholder="usuario@empresa.cl" className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Contraseña
                {configGlobal.inputsoftPasswordSet && (
                  <span className="text-emerald-600 ml-1 font-normal">(guardada ✓)</span>
                )}
              </label>
              <input type="password" value={configGlobal.inputsoftPassword}
                onChange={e => setConfigGlobal(p => ({ ...p, inputsoftPassword: e.target.value }))}
                placeholder={configGlobal.inputsoftPasswordSet ? '(sin cambios)' : 'Contraseña'}
                className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <button onClick={guardarConfigGlobal} disabled={guardandoGlobal}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50">
            {guardandoGlobal ? 'Guardando...' : 'Guardar credenciales'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TARJETA DE LISTA
// ─────────────────────────────────────────────────────────────────────────────
function ListaCard({ lista, mes, anio, configMap, onAbrir, onEdit, onDelete, onEjecutar }) {
  const [showTipo, setShowTipo] = useState(false);

  const totalEmpresas = lista.empresas?.length || 0;
  const configuradas  = (lista.empresas || []).filter(e => configMap[(e._id || e).toString()]?.inputsoftEmpresaId).length;
  const puedeEjecutar = configuradas > 0;

  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden flex flex-col">
      {/* Cuerpo — clic abre detalle */}
      <button onClick={onAbrir} className="p-4 flex-1 text-left hover:bg-gray-50 transition-colors">
        <h3 className="font-semibold text-gray-800 text-base mb-1 leading-tight">{lista.nombre}</h3>
        {lista.descripcion && (
          <p className="text-xs text-gray-500 mb-3">{lista.descripcion}</p>
        )}
        <div className="flex items-center gap-3 text-xs mb-3">
          <span className="text-gray-500">{totalEmpresas} empresa{totalEmpresas !== 1 ? 's' : ''}</span>
          {totalEmpresas > 0 && (
            <span className={configuradas === totalEmpresas ? 'text-green-600 font-medium' : 'text-orange-500'}>
              {configuradas}/{totalEmpresas} con ID
            </span>
          )}
        </div>
        {(lista.empresas || []).length > 0 && (
          <div className="space-y-0.5">
            {lista.empresas.slice(0, 4).map(e => (
              <div key={e._id || e} className="text-xs text-gray-500 truncate leading-relaxed">
                · {e.nombre || '—'}
              </div>
            ))}
            {lista.empresas.length > 4 && (
              <div className="text-xs text-gray-400 italic">+{lista.empresas.length - 4} más…</div>
            )}
          </div>
        )}
        <div className="mt-3 text-xs text-blue-600 font-medium">Abrir →</div>
      </button>

      {/* Acciones */}
      <div className="border-t bg-gray-50 px-4 py-3">
        {!showTipo ? (
          <div className="flex gap-2">
            <button onClick={() => setShowTipo(true)} disabled={!puedeEjecutar}
              title={!puedeEjecutar ? 'Configura al menos una empresa primero' : `Ejecutar ${MESES[mes]} ${anio}`}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              ▶ Ejecutar
            </button>
            <button onClick={onEdit} title="Editar lista"
              className="px-3 py-1.5 border border-gray-300 hover:bg-white text-gray-500 text-xs rounded-lg transition-colors">✏</button>
            <button onClick={onDelete} title="Eliminar lista"
              className="px-3 py-1.5 border border-gray-300 hover:bg-red-50 hover:border-red-300 hover:text-red-500 text-gray-400 text-xs rounded-lg transition-colors">🗑</button>
          </div>
        ) : (
          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">
              ¿Qué ejecutar? — <span className="text-blue-600">{MESES[mes]} {anio}</span>
            </p>
            <div className="flex gap-1.5 flex-wrap">
              {['compras', 'ventas', 'ambos'].map(tipo => (
                <button key={tipo} onClick={() => { onEjecutar(tipo); setShowTipo(false); }}
                  className="flex-1 py-1.5 text-xs font-semibold rounded-lg capitalize bg-blue-600 hover:bg-blue-700 text-white transition-colors min-w-0">
                  {tipo}
                </button>
              ))}
              <button onClick={() => setShowTipo(false)}
                className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-600 rounded-lg border">✕</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DETALLE DE LISTA (vista completa al abrir una lista)
// ─────────────────────────────────────────────────────────────────────────────
function ListaDetalle({ lista, mes, anio, rentaCompanies, configMap, logs, usuarios, isAdmin, onBack, onEdit, onDelete, onEjecutar, onGuardado }) {
  const [subTab,         setSubTab]         = useState('empresas');
  const [expandedConfig, setExpandedConfig] = useState(null);
  const [showTipo,       setShowTipo]       = useState(false);

  const empresasLista = (lista.empresas || []).map(e => {
    if (e.nombre) return e;
    return rentaCompanies.find(c => c._id === (e._id || e)) || { _id: e._id || e, nombre: '—', rut: '' };
  });

  const totalEmpresas = empresasLista.length;
  const configuradas  = empresasLista.filter(e => configMap[(e._id || e._id?.toString())]?.inputsoftEmpresaId).length;
  const puedeEjecutar = configuradas > 0;

  const idsLista  = new Set(empresasLista.map(e => (e._id || e).toString()));
  const logsLista = logs.filter(l => l.clienteId?._id && idsLista.has(l.clienteId._id.toString()));

  return (
    <div>
      {/* Cabecera */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
          ← Mis Listas
        </button>
        <span className="text-gray-300">/</span>
        <h2 className="font-semibold text-gray-800 text-base truncate">{lista.nombre}</h2>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-400 hidden sm:inline">{totalEmpresas} empresa{totalEmpresas !== 1 ? 's' : ''}</span>
          <button onClick={onEdit}
            className="px-3 py-1.5 text-xs border rounded-lg text-gray-500 hover:bg-gray-50 transition-colors">
            ✏ Editar lista
          </button>
          <button onClick={onDelete}
            className="px-3 py-1.5 text-xs border rounded-lg text-red-400 hover:bg-red-50 hover:border-red-300 transition-colors">
            🗑
          </button>
        </div>
      </div>

      {/* Sub-tabs + botón ejecutar */}
      <div className="flex items-end gap-0.5 border-b mb-5">
        {[
          { id: 'empresas',  label: `🏢 Empresas (${totalEmpresas})` },
          { id: 'historial', label: `📋 Historial (${logsLista.length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              subTab === t.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {t.label}
          </button>
        ))}
        <div className="ml-auto flex items-center pb-1">
          {!showTipo ? (
            <button onClick={() => setShowTipo(true)} disabled={!puedeEjecutar}
              title={!puedeEjecutar ? 'Configura al menos una empresa primero' : ''}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              ▶ Ejecutar lista
            </button>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500 mr-1">¿Qué ejecutar?</span>
              {['compras', 'ventas', 'ambos'].map(tipo => (
                <button key={tipo} onClick={() => { onEjecutar(tipo); setShowTipo(false); }}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg capitalize bg-blue-600 hover:bg-blue-700 text-white transition-colors">
                  {tipo}
                </button>
              ))}
              <button onClick={() => setShowTipo(false)}
                className="px-2 py-1.5 text-xs text-gray-400 hover:text-gray-600 rounded-lg border">✕</button>
            </div>
          )}
        </div>
      </div>

      {/* Sub-tab: Empresas */}
      {subTab === 'empresas' && (
        empresasLista.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border">
            <p className="text-gray-400 text-sm">Esta lista no tiene empresas.</p>
            <button onClick={onEdit} className="mt-3 text-blue-600 text-sm hover:underline">
              Editar lista para agregar empresas
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {empresasLista.map(e => {
              const id        = (e._id || e).toString();
              const cfg       = configMap[id];
              const isExpanded = expandedConfig === id;
              const tieneId   = !!cfg?.inputsoftEmpresaId;
              return (
                <div key={id} className="bg-white border rounded-xl overflow-hidden shadow-sm">
                  <button onClick={() => setExpandedConfig(isExpanded ? null : id)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${tieneId ? 'bg-green-500' : 'bg-orange-400'}`} />
                      <div className="min-w-0">
                        <div className="font-medium text-gray-800 text-sm truncate">{e.nombre}</div>
                        <div className="text-xs text-gray-400 font-mono">{e.rut}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      {tieneId
                        ? <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full font-medium hidden sm:inline">#{cfg.inputsoftEmpresaId}</span>
                        : <span className="text-xs px-2 py-0.5 bg-orange-50 text-orange-600 border border-orange-200 rounded-full hidden sm:inline">Sin configurar</span>
                      }
                      <span className="text-gray-400 text-xs">{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t bg-gray-50/50">
                      <div className="pt-4">
                        <ConfigEmpresaInline
                          clienteId={id}
                          initialConfig={cfg}
                          usuarios={usuarios}
                          isAdmin={isAdmin}
                          onGuardado={onGuardado}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Sub-tab: Historial */}
      {subTab === 'historial' && <HistorialLogs logs={logsLista} onRefresh={onGuardado} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EDITOR DE LISTA (crear / editar)
// ─────────────────────────────────────────────────────────────────────────────
function ListaEditor({ lista, companies, configMap, onSave, onCancel }) {
  const [nombre,      setNombre]      = useState(lista.nombre      || '');
  const [descripcion, setDescripcion] = useState(lista.descripcion || '');
  const [selectedIds, setSelectedIds] = useState(
    new Set((lista.empresas || []).map(e => (e._id || e).toString()))
  );
  const [search,  setSearch]  = useState('');
  const [saving,  setSaving]  = useState(false);

  const filtered = companies.filter(c =>
    !search ||
    c.nombre.toLowerCase().includes(search.toLowerCase()) ||
    c.rut.includes(search)
  );

  const toggle = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (!nombre.trim()) return toast.error('El nombre es requerido');
    setSaving(true);
    await onSave({
      _id:         lista._id,
      nombre:      nombre.trim(),
      descripcion: descripcion.trim(),
      empresas:    Array.from(selectedIds),
    });
    setSaving(false);
  };

  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
      {/* Cabecera */}
      <div className="px-4 sm:px-6 py-4 border-b flex items-center justify-between">
        <h2 className="font-semibold text-gray-800 text-base">
          {lista._id ? 'Editar lista' : 'Nueva lista'}
        </h2>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
      </div>

      <div className="px-4 sm:px-6 py-4 space-y-4">
        {/* Nombre + descripción */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Nombre *</label>
            <input
              type="text"
              value={nombre}
              onChange={e => setNombre(e.target.value)}
              placeholder="ej: IVA mensual, RCV enero, F29…"
              autoFocus
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Descripción</label>
            <input
              type="text"
              value={descripcion}
              onChange={e => setDescripcion(e.target.value)}
              placeholder="Opcional…"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none" />
          </div>
        </div>

        {/* Selector de empresas */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-500">
              Empresas ({selectedIds.size} seleccionadas)
            </label>
            {selectedIds.size > 0 && (
              <button onClick={() => setSelectedIds(new Set())}
                className="text-xs text-gray-400 hover:text-gray-600 underline">
                Limpiar selección
              </button>
            )}
          </div>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar empresa…"
            className="w-full border rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none" />
          <div className="max-h-72 overflow-y-auto border rounded-lg divide-y">
            {filtered.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">Sin resultados</p>
            ) : filtered.map(c => {
              const id       = c._id.toString();
              const selected = selectedIds.has(id);
              const tieneId  = !!configMap[c._id]?.inputsoftEmpresaId;
              return (
                <label key={c._id}
                  className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                    selected ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}>
                  <input type="checkbox" checked={selected} onChange={() => toggle(id)}
                    className="accent-blue-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-gray-800 truncate">{c.nombre}</div>
                    <div className="text-xs text-gray-400 font-mono">{c.rut}</div>
                  </div>
                  {tieneId ? (
                    <span className="text-xs text-green-600 font-medium flex-shrink-0">✓</span>
                  ) : (
                    <span className="text-xs text-orange-400 flex-shrink-0">sin ID</span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 sm:px-6 py-3 border-t bg-gray-50 flex flex-wrap gap-2">
        <button onClick={handleSave} disabled={saving}
          className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors">
          {saving ? 'Guardando…' : lista._id ? 'Actualizar lista' : 'Crear lista'}
        </button>
        <button onClick={onCancel}
          className="px-4 py-2 border text-gray-500 hover:bg-white text-sm rounded-lg transition-colors">
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG POR EMPRESA (inline expandible)
// ─────────────────────────────────────────────────────────────────────────────
function ConfigEmpresaInline({ clienteId, initialConfig, usuarios, isAdmin, onGuardado }) {
  const [cfg, setCfg] = useState({
    inputsoftEmpresaId:  initialConfig?.inputsoftEmpresaId  || '',
    sucursalId:          initialConfig?.sucursalId          || '',
    cuentaDefaultCompra: initialConfig?.cuentaDefaultCompra || '',
    cuentaDefaultVenta:  initialConfig?.cuentaDefaultVenta  || '',
    modoImpuestos:       initialConfig?.modoImpuestos       || 'exento',
    impuestosAMantener:  (initialConfig?.impuestosAMantener || []).join(', '),
    activoBatch:         initialConfig?.activoBatch         || false,
    usuariosAsignados:   (initialConfig?.usuariosAsignados  || []).map(u => u._id || u),
  });
  const [guardando, setGuardando] = useState(false);
  const [subTab,    setSubTab]    = useState('config');

  const guardar = async () => {
    if (!cfg.inputsoftEmpresaId.trim()) return toast.error('El ID Inputsoft es obligatorio');
    setGuardando(true);
    try {
      const payload = {
        ...cfg,
        impuestosAMantener: cfg.modoImpuestos === 'mantener_especificos'
          ? cfg.impuestosAMantener.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        usuariosAsignados: cfg.usuariosAsignados,
      };
      await api.put(`/contabilidad/empresas/${clienteId}`, payload);
      toast.success('Configuración guardada');
      onGuardado();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error guardando');
    } finally {
      setGuardando(false);
    }
  };

  const toggleUsuario = (userId) => {
    setCfg(p => ({
      ...p,
      usuariosAsignados: p.usuariosAsignados.includes(userId)
        ? p.usuariosAsignados.filter(id => id !== userId)
        : [...p.usuariosAsignados, userId]
    }));
  };

  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex gap-3 mb-4 border-b border-blue-100 overflow-x-auto">
        {[['config', 'Datos contables'], ['usuarios', 'Contadores'], ['reglas', 'Reglas']].map(([id, label]) => (
          <button key={id} onClick={() => setSubTab(id)}
            className={`pb-2 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap ${
              subTab === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {subTab === 'config' && (
        <div className="space-y-4">
          {/* IDs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">ID Inputsoft *</label>
              <input type="text" value={cfg.inputsoftEmpresaId}
                onChange={e => setCfg(p => ({ ...p, inputsoftEmpresaId: e.target.value }))}
                placeholder="ej: 125" className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">ID Sucursal</label>
              <input type="text" value={cfg.sucursalId}
                onChange={e => setCfg(p => ({ ...p, sucursalId: e.target.value }))}
                placeholder="vacío = CASA MATRIZ" className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Cta. default Compra</label>
              <input type="text" value={cfg.cuentaDefaultCompra}
                onChange={e => setCfg(p => ({ ...p, cuentaDefaultCompra: e.target.value }))}
                placeholder="ej: 4110" className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Cta. default Venta</label>
              <input type="text" value={cfg.cuentaDefaultVenta}
                onChange={e => setCfg(p => ({ ...p, cuentaDefaultVenta: e.target.value }))}
                placeholder="ej: 7100" className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
          </div>

          {/* Modo impuestos */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Manejo de impuestos en CSV</label>
            <div className="space-y-2">
              {[
                { value: 'exento',            label: 'Todo a exento',             desc: 'Suma todos los impuestos en la columna Exento.' },
                { value: 'columnas_xyz',       label: 'Columnas de impuesto (XYZ)',desc: 'Conserva las columnas código/valor/tasa tal como vienen del SII.' },
                { value: 'mantener_especificos', label: 'Mantener impuestos específicos', desc: 'Los códigos indicados conservan columnas; el resto va a Exento.' },
              ].map(opt => (
                <label key={opt.value} className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" value={opt.value} checked={cfg.modoImpuestos === opt.value}
                    onChange={() => setCfg(p => ({ ...p, modoImpuestos: opt.value }))}
                    className="accent-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="flex-1">
                    <span className="text-sm font-medium">{opt.label}</span>
                    <span className="block text-xs text-gray-400">{opt.desc}</span>
                    {opt.value === 'mantener_especificos' && cfg.modoImpuestos === 'mantener_especificos' && (
                      <input type="text" value={cfg.impuestosAMantener}
                        onChange={e => setCfg(p => ({ ...p, impuestosAMantener: e.target.value }))}
                        placeholder="ej: 14, 15  (separados por coma)"
                        className="mt-1.5 w-full max-w-xs border rounded px-2 py-1 text-xs" />
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Batch (solo admin) */}
          {isAdmin && (
            <div className="flex items-start gap-2 pt-1">
              <input type="checkbox" id={`batch-${clienteId}`} checked={cfg.activoBatch}
                onChange={e => setCfg(p => ({ ...p, activoBatch: e.target.checked }))}
                className="accent-blue-600 mt-0.5" />
              <label htmlFor={`batch-${clienteId}`} className="text-sm text-gray-600 cursor-pointer">
                <span className="font-medium">Incluir en ejecución por lote</span>
                <span className="block text-xs text-gray-400">Se ejecutará en el procesamiento masivo batch.</span>
              </label>
            </div>
          )}

          {isAdmin && (
            <button onClick={guardar} disabled={guardando}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-4 py-1.5 rounded-lg disabled:opacity-50">
              {guardando ? 'Guardando…' : 'Guardar configuración'}
            </button>
          )}
        </div>
      )}

      {subTab === 'usuarios' && (
        <div>
          <p className="text-xs text-gray-500 mb-3">
            Contadores que pueden ver y ejecutar esta empresa.
            {!isAdmin && <span className="block text-orange-500 mt-1">Solo el administrador puede modificar.</span>}
          </p>
          {usuarios.length === 0 ? (
            <p className="text-xs text-gray-400">No hay usuarios disponibles.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-4">
              {usuarios.map(u => {
                const assigned = cfg.usuariosAsignados.includes(u._id);
                return (
                  <label key={u._id}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                      assigned ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                    } ${!isAdmin ? 'pointer-events-none opacity-70' : ''}`}>
                    <input type="checkbox" checked={assigned} disabled={!isAdmin}
                      onChange={() => toggleUsuario(u._id)} className="accent-blue-600" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-800 truncate">{u.nombre}</span>
                      <span className="block text-xs text-gray-400">{u.role}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          {isAdmin && (
            <button onClick={guardar} disabled={guardando}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-4 py-1.5 rounded-lg disabled:opacity-50">
              {guardando ? 'Guardando…' : 'Guardar asignación'}
            </button>
          )}
        </div>
      )}

      {subTab === 'reglas' && <ReglasContables clienteId={clienteId} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HISTORIAL DE EJECUCIONES
// ─────────────────────────────────────────────────────────────────────────────
function HistorialLogs({ logs, onRefresh }) {
  const [logDetalle, setLogDetalle] = useState(null);

  const verDetalle = async (logId) => {
    try {
      const { data } = await api.get(`/contabilidad/logs/${logId}`);
      setLogDetalle(data);
    } catch (_) { toast.error('Error cargando detalle'); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-base font-semibold text-gray-700">Historial de ejecuciones</h2>
        <button onClick={onRefresh} className="text-sm text-blue-600 hover:text-blue-800">↻ Actualizar</button>
      </div>

      {logs.length === 0 ? (
        <p className="text-gray-400 text-sm py-8 text-center">No hay ejecuciones registradas.</p>
      ) : (
        <div className="bg-white border rounded-xl overflow-hidden shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[500px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Empresa</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Período</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Tipo</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const cfg = estadoConfig[log.estado] || estadoConfig.error;
                return (
                  <tr key={log._id} onClick={() => verDetalle(log._id)}
                    className="border-t hover:bg-gray-50 cursor-pointer transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-800 max-w-[180px] truncate">
                      {log.clienteId?.nombre || log.nombreEmpresa}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{MESES[log.mes]} {log.anio}</td>
                    <td className="px-4 py-3 text-gray-600 capitalize">{log.tipoDocumento}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full inline-block ${cfg.dot}`} />
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {new Date(log.fechaInicio).toLocaleString('es-CL')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {logDetalle && (
        <div className="mt-4 bg-gray-900 text-gray-100 rounded-xl p-4 text-xs font-mono overflow-x-auto">
          <div className="flex justify-between items-center mb-3">
            <span className="font-bold text-emerald-400">
              {logDetalle.clienteId?.nombre || logDetalle.nombreEmpresa} — {MESES[logDetalle.mes]} {logDetalle.anio} — {logDetalle.tipoDocumento}
            </span>
            <button onClick={() => setLogDetalle(null)} className="text-gray-400 hover:text-white ml-4">✕</button>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {[['descargaSii', 'Descarga SII'], ['procesamientoCsv', 'CSV'], ['subidaInputsoft', 'Inputsoft']].map(([key, label]) => {
              const etapa = logDetalle[key];
              if (!etapa) return null;
              return (
                <div key={key} className={`px-3 py-1 rounded text-xs font-medium ${
                  etapa.ok ? 'bg-green-800 text-green-100' : etapa.ok === false ? 'bg-red-800 text-red-100' : 'bg-gray-700 text-gray-300'
                }`}>
                  {etapa.ok ? '✓' : etapa.ok === false ? '✗' : '…'} {label}
                  {etapa.filasProcesadas != null && ` (${etapa.filasProcesadas} f.)`}
                </div>
              );
            })}
          </div>
          <div className="max-h-72 overflow-y-auto space-y-0.5 pr-1">
            {(logDetalle.logEntries || []).map((entry, i) => (
              <div key={i} className="text-gray-300 leading-relaxed">{entry}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
